<template>
    <div>
        <vue-headful v-if="loading" title="Inspection Chart :: MeritEHS" />
        <vue-headful v-else :title="'Inspection Chart - '+(this.filters.inspection_form_id && this.forms[this.filters.inspection_form_id] ? this.forms[this.filters.inspection_form_id].form_name : '')+' - ' + today + ' :: MeritEHS'" />

        <h4 class="py-3 mb-3 d-print-none">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item active">Inspection Charts</li>
            </ol>
        </h4>

        <div :class="pdf_view ? 'container' : ''" :style="pdf_view ? 'max-width: 980px;' : ''">
            <div class="row d-none d-print-block" v-if="pdfLogo" style="margin-bottom: -20px; z-index: 100;">
                <div class="col-md-3">
                    <img :src="pdfLogo" class="img-fluid" />
                </div>
            </div>

            <div class="alert alert-info d-print-none" v-if="pdf_view">
                <strong>How to Export as PDF:</strong>
                <ol>
                    <li>On your browser, there should be a "File" or "Options" tab with a "Print" option. You may also hold down the "Ctrl" key and press the "P" key.</li>
                    <li>This will bring up the print page options box. There should be an option called "Select Printer" or "Destination".</li>
                    <li>Select "Save as PDF" or "Adobe PDF".</li>
                    <li>Click "Save" or "Print". This will open your file explorer and let you save it to your computer as a PDF.</li>
                </ol>
            </div>

            <div class="card">
                <div class="card-body">

                    <div class="d-print-none mb-4">
                        <button class="btn btn-sm btn-default" v-on:click="show_filters = !show_filters" title="Show/Hide Filters"><i class="fa fa-filter"></i>
                            <span class="badge" style="background-color: #d9534f" :title="`${filterCount} Filter(s) Applied`" v-if="filterCount > 0">{{ filterCount }}</span>
                        </button>

                        Inspection Filters:
                        <div v-show="show_filters" class="mt-3">
                            <div class="d-inline-block input-group-sm">
                                <select v-model="filters.inspection_form_id" class="form-control mr-2">
                                    <option :value="null">-- Show All Forms --</option>
                                    <option v-for="form in forms" v-bind:value="form.inspection_form_id">
                                        {{ form.form_name }}
                                    </option>
                                </select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters['interval']"  :btnLabel="(values) => ('Interval' + (values.length > 0 ? ' - ' + values[0].name : ''))" :selectOptions="intervalOptions" :options="{multi: false}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters['graph_by']" :btnLabel="(values) => ('Graph By' + (values.length > 0 ? ' - ' + values[0].name : ''))" :selectOptions="graphByOptions" :options="{multi: false}"></multi-select>
                            </div>

                            <template v-if="filters.inspection_form_id">
                                <div class="d-inline-block">
                                    <multi-select v-model="filters['sections']" :btnLabel="(values) => ('Sections ('+values.length+')')" :selectOptions="sections" :options="{multi: true}"></multi-select>
                                </div>
                                <div class="d-inline-block">
                                    <multi-select v-model="filters['elements']" :btnLabel="(values) => ('Elements ('+values.length+')')" :selectOptions="elements" :options="{multi: true, groups: true}"></multi-select>
                                </div>
                            </template>

                            <div class="d-inline-block">
                                <multi-select v-model="filters['jobsite.JobsiteId']" :btnLabel="(values) => ('Jobsites ('+values.length+')')" :selectOptions="jobsites" :options="{multi: true}" :search="true"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <multi-select v-model="filters['divisions']" :btnLabel="(values) => ('Divisions ('+values.length+')')" :selectOptions="divisions" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <date-range-selector ref="inspDate" buttonTitle="Inspection Date" v-model="filters['date_range']" />
                            </div>

                            <div style="margin-top: 20px;">
                                <button type="button" class="btn btn-sm btn-default" v-on:click="closeFilters()">
                                    <i class="fa fa-caret-up"></i> Close
                                </button>
                                <button type="button" class="btn btn-sm btn-warning" v-on:click="clearFilters()">
                                    <i class="fa fa-times"></i> Clear
                                </button>
                                <button type="button" class="btn btn-sm btn-success" v-on:click="applyFilters()">
                                    <i class="fa fa-filter"></i> Apply
                                </button>
                                <div class="d-inline-block ml-2">
                                    <b-form-checkbox v-model="pdf_view" :value="true" :unchecked-value="false" plain class="ml-2">PDF View</b-form-checkbox>
                                </div>
                                <div class="d-inline-block ml-2">
                                    <b-form-checkbox v-model="hide_empty" :value="true" :unchecked-value="false" plain class="ml-2">Hide Empty Intervals/Sections</b-form-checkbox>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="loading">
                        <i class="fa fa-spin fa-spinner"></i> Initializing
                    </div>
                    <div v-else>
                        <highcharts id="highcharts-canvas" :options="options" style="min-height: 800px;" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import filterExtractor from '@/helpers/TableFilterExtractor';
    import moment from 'moment-timezone';

    export default {
        data: function() {
            return {
                series: [],
                labels: [],
                loading: true,
                hide_empty: false,
                pdf_view: false,
                show_filters: true,
                filterCount: 0,
                dateLabel: null,
                pdfLogo: this.$store.state.user.pdf_logo,

                intervalOptions: [
                    {id: 'Week', name: 'Weekly'},
                    {id: 'Month', name: 'Monthly'},
                    {id: 'Year', name: 'Yearly'}
                ],

                filters: {
                    'interval': [{'name': 'Monthly', 'id': 'Month'}],
                    'sections': [],
                    'elements': [],
                    'divisions': [],
                    'jobsite.JobsiteId': [],
                    'date_range': 'ytd',
                    'graph_by': [{'id': 'InspectionSectionId', 'name': 'Section'}],
                    'inspection_form_id': null,
                },

                graphByOptions: [
                    {id: 'InspectionSectionId', name: 'Section'},
                    {id: 'InspectionElementId', name: 'Element'},
                ],

                forms: [],
                sections: [],
                elements: [],
                jobsites: [],
                divisions: [],
            }
        },

        computed: {
            today(){
                //Send todays date
                return moment().format("YYYY-MM-DD");
            },

            labelCompliant(){
                //Setup
                var id = this.filters.inspection_form_id;

                //Check for Result Labels
                if(id && this.forms[id].result_labels && this.forms[id].result_labels.compliant)
                {
                    return this.forms[id].result_labels.compliant;
                }

                return 'Compliant';
            },

            labelNonCompliant(){
                //Setup
                var id = this.filters.inspection_form_id;

                //Check for Result Labels
                if(id && this.forms[id].result_labels && this.forms[id].result_labels.noncompliant)
                {
                    return this.forms[id].result_labels.noncompliant;
                }

                //Default
                return 'Non-Compliant';
            },

            options(){
                //Setup
                var id = this.filters.inspection_form_id;

                return {
                    chart: {
                        type: 'bar',
                    },
                    title: {
                        text: 'Inspections' + (id && this.forms[id] ? ' - ' + this.forms[id].form_name : '') + ': '+this.dateLabel,
                    },
                    xAxis: {
                        categories: this.labels,
                    },
                    yAxis: {
                        title: {
                            text: '# of '+this.labelNonCompliant+' Items'
                        },
                        //tickInterval: 1,
                    },
                    plotOptions: {
                        bar: {
                            dataLabels: {
                                enabled: true,
                                formatter: function(){
                                    return this.point.series.name+': '+this.point.y;
                                }
                            },
                        }
                    },
                    legend: {
                        layout: 'horizontal',
                        align: 'right',
                        verticalAlign: 'bottom',
                        x: 0,
                        y: 0,
                        floating: false,
                        borderColor: '#dddddd',
                        borderWidth: 1
                    },
                    exporting: {
                        enableImages: true,
                        width: 1000,
                    },
                    tooltip: {
                        formatter() {
                            //Send it
                            var html = '<b>'+ this.series.name + '</b><br/>' +
                                    'Date: <b>'+ this.x + '</b><br/>' +
                                    'Total: <b>'+ this.y + '</b><br/>';
                                    //'Total '+this.labelNonCompliant+': <b>'+ this.y + '</b><br/>';

                            //Check for empty or undefined
                            if(typeof this.point.options.items == 'undefined' || Object.keys(this.point.options.items).length == 0)
                            {
                                return html;
                            }

                            //Setup
                            var elemKeys = Object.keys(this.point.options.items);

                            //Loop and Add items
                            for(const key of elemKeys)
                            {
                                //Helper
                                var elem = this.point.options.items[key];

                                //Append the HTML
                                html += key+' '+elem.text + ': <b>' + elem.total + '</b><br>';
                            };

                            //Send it
                            return html;
                        }
                    },
                    series: this.series
                }
            },

        },

        watch: {
            //Watch the Inspection Form ID
            'filter.inspection_form_id'(newVal, oldValu){
                //Get the Filter
                this.getFilters();

                //Re-Fetch the Data
                this.getData();
            },

            hide_empty(newVal, oldValue){
                //Reload the Data
                this.getData();
            },

            pdf_view(newVal, oldVal){
                //Helper?
                this.loading = true;

                //Redraw the Draw
                this.getData();
            },

            'filters.date_range'(newVal, oldVal){
                //Set the label
                this.setDateLabel();
            },
        },

        methods: {

            setDateLabel(){
                //Set the Label
                this.dateLabel = this.$refs.inspDate.getLabel();
            },

            customLabel ({ JobsiteNumber, JobsiteName }) {
                //Send it
                return `${JobsiteNumber} – ${JobsiteName}`;
            },

            applyFilters() {
                //Update the count
                this.updateFilterCount();

                //Get the data
                this.getData();
            },

            closeFilters() {
                //Hide the modal
                this.show_filters = false;
            },

            updateFilterCount() {
                //Reset the count
                this.filterCount = 0;
                var status = false;

                // Loop over objects in this.filters
                for (var obj in this.filters) {

                    if (this.filters[obj] == null || this.filters[obj] === '') {
                        continue;
                    }

                    if (Array.isArray(this.filters[obj])) {
                        if (this.filters[obj].length > 0) {
                            this.filterCount++;
                        }
                        continue;
                    }

                    if (typeof this.filters[obj] === 'object') {
                        var Empty = true;
                        for (var key in this.filters[obj]) {
                            if (this.filters[obj].hasOwnProperty(key));
                            Empty = false;
                        }
                        if (!Empty) {
                            this.filterCount++;
                        }
                        continue;
                    }

                    if (typeof this.filters[obj] === 'string' || typeof this.filters[obj] === 'number') {
                        this.filterCount++;
                    }
                }
            },

            getData(callback = null) {
                //Setup
                var filters = filterExtractor.extractFilters(this.filters);

                //Append Basic Stuff
                filters['hide_empty'] = this.hide_empty;

                this.$http
                    .get('reports/inspection-stats', filters)
                    .then(response => {
                        //Check for length
                        if(response.data.series.length === 0)
                        {
                            //Alert the user
                            this.$toastr.warning('There is no data for your currently selected filters.')

                            //Stop here
                            return;
                        }

                        //Set all the data
                        this.labels = response.data.labels;
                        this.series = response.data.series;
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;

                        //Call the helper
                        if(callback)
                        {
                            callback();
                        }
                    });
            },

            getFilters() {

                //Send the response
                this.$http
                    .get('reports/filters/inspections', this.filters)
                    .then(response => {
                        //Set all the data
                        //this.filters.inspection_form_id = response.data.default_form_id;
                        this.forms = response.data.forms;
                        this.jobsites = response.data.jobsites;
                        this.divisions = response.data.divisions;
                        this.injury_types = response.data.injury_types;
                        this.incident_types = response.data.incident_types;
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },
        },

        mounted() {
            //Get the forms
            //this.getForms();

            //Set the Date Label
            this.setDateLabel();

            //Loda the Filters
            this.getFilters();

            //Graph it
            this.getData();
        }
    }
</script>

<style type="text/css" scoped>
    @media print{
        /*@page {
            size: landscape;
        }*/

        #highcharts-canvas {
            width: 2300;
        }

        .card {
            background-color: transparent;
            border: none;
        }
    }
</style>
